import classNames from 'classnames';
import { Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { FiAlertCircle } from 'react-icons/fi';

import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';
import DeleteButton from 'components/common/button/DeleteButton';

import * as routes from 'constants/routes';
import { DEFAULT_HISTORY_ENTRIES } from 'constants/appConstants';

import * as designationService from 'services/designation';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { handleError } from 'utils/errorHandler';

import designationSchema from 'schemas/designationSchema';

import type { Designation } from 'interface/designation';

type DesignationFormValues = Required<Pick<Designation, 'name'>> & {
  id?: number;
  description: string | null;
  status: boolean;
};

interface Props {
  designation?: Designation;
}

type ModalButton = {
  text: string;
  type: string;
  className?: string;
  onClick?: () => void | Promise<void>;
  close?: boolean;
  showLoading?: boolean;
};

function DesignationForm(props: Props) {
  const { designation } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues: DesignationFormValues = useMemo(() => {
    return {
      id: designation?.id,
      name: designation?.name ?? '',
      description: designation?.description ?? null,
      status: designation?.status ?? true,
    };
  }, [designation]);

  const handleCancelClick = () => {
    return history.length > DEFAULT_HISTORY_ENTRIES ? history.goBack() : history.push(routes.DESIGNATION);
  };

  const handleSubmit = async (values: DesignationFormValues) => {
    setIsSubmitting(true);

    try {
      const designationObject: Designation = {
        id: designation?.id || undefined,
        name: values.name?.trim(),
        description: values.description ? values.description.trim() : null,
        status: values.status || true,
      };

      if (designation?.id != null) {
        await designationService.update(designationObject, designation.id);
      } else {
        await designationService.create(designationObject);
      }

      toast.success({
        title: 'Success',
        message: designation?.id != null ? 'Designation updated successfully.' : 'Designation created successfully.',
      });

      history.push(routes.DESIGNATION);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (designation?.id == null) {
      history.push(routes.DESIGNATION);
      return;
    }

    try {
      await designationService.remove(designation.id);

      toast.success({
        title: 'Success',
        message: 'Designation deleted successfully.',
      });
    } catch (error) {
      handleError(error);
    } finally {
      history.push(routes.DESIGNATION);
    }
  };

  const buttons: ModalButton[] = [
    {
      text: 'Confirm',
      type: 'red',
      className: 'mr-12 delete__btn',
      onClick: handleDelete,
      close: true,
      showLoading: true,
    },
    {
      text: 'Cancel',
      type: 'outlined-grey',
      className: 'py-8 px-12',
      close: true,
    },
  ];

  return (
    <Formik<DesignationFormValues>
      initialValues={initialValues}
      validationSchema={designationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleChange, handleSubmit, touched, errors, values, handleBlur }) => (
        <form onSubmit={handleSubmit} className="notice-form">
          <div className="full-scope-card__content">
            <div className="form-wrap">
              <div className="form-wrap__row form-wrap__row--no-margin clearfix">
                <div className="form-wrap__col col-sm-6 px-0">
                  <div className="form-group width-full">
                    <label className="form-label dark--text">
                      Designation Name <span className="required ml-5">*</span>
                    </label>
                    <input
                      name="name"
                      type="text"
                      value={values.name}
                      placeholder="Designation Name"
                      className={classNames('form-elem form-elem--input-sm', {
                        'form-elem--error': Boolean(errors.name) && Boolean(touched.name),
                      })}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    {errors.name && touched.name && <div className="error-block">{errors.name}</div>}
                  </div>
                  <div className="form-group width-full">
                    <label className="form-label dark--text">Designation Description</label>
                    <textarea
                      name="description"
                      value={values.description ?? ''}
                      placeholder="Designation Description"
                      className={classNames('form-elem form-elem--input-sm', {
                        'form-elem--error': Boolean(errors.description) && Boolean(touched.description),
                      })}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    {errors.description && touched.description && (
                      <div className="error-block">{String(errors.description)}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="action-bar-footer action-bar-footer--bordered-top px-16">
              <button
                type="submit"
                className={classNames('btn btn--primary btn--curved mr-10', {
                  'btn--loading': isSubmitting,
                })}
                disabled={isSubmitting}
              >
                <span className={isSubmitting ? 'invisible' : ''}>Save</span>
                {isSubmitting && <Loading />}
              </button>
              <button
                type="button"
                className={classNames('btn btn--outlined-grey btn--curved mr-10')}
                disabled={isSubmitting}
                onClick={handleCancelClick}
              >
                Cancel
              </button>

              {designation?.id != null && (
                <LFModal
                  TriggerBtn={DeleteButton}
                  modalStatusIcon={<FiAlertCircle color="#F44336" size={24} />}
                  title={{ text: 'Delete this designation?', type: 'danger' }}
                  className="delete-modal"
                  message={{ text: "You won't be able to revert this!", type: 'delete__text' }}
                  renderFooter={true}
                  buttons={buttons}
                />
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}

export default DesignationForm;
